import type { CategoricalDetailMatrix } from "../editor/model_detail"
import { categoryGroupKeyForLabel } from "../editor/model_detail"
import { parseCsvLine } from "../modelling/payoff_matrix_csv"

export type CategoricalDetailMatrixCsvUpdate = {
  nodeId: number
  categoryValue: number
  numericValue: string
}

function normalizeLabelKey(label: string): string {
  return label.trim().toLowerCase()
}

function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes("\n") || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function parseCsvRows(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim()
  if (!normalized) {
    return []
  }
  return normalized.split("\n").map(line => parseCsvLine(line))
}

/**
 * Matrix CSV layout (matches grouped Detail table):
 * - Row 1: empty corner cell, then component names as column headers
 * - Following rows: category label, then numeric values per component
 */
export function parseCategoricalDetailMatrixCsv(
  csvText: string,
  matrix: CategoricalDetailMatrix
): CategoricalDetailMatrixCsvUpdate[] {
  const rows = parseCsvRows(csvText)
  if (rows.length < 2 || rows[0].length < 2) {
    return []
  }

  const header = rows[0]
  const columnKeys = header.slice(1).map(cell => normalizeLabelKey(cell))
  const updates: CategoricalDetailMatrixCsvUpdate[] = []

  // Per-label cursor: the matrix may carry multiple rows for the same
  // category label when sibling components disagreed on its value (see
  // `buildCategoricalDetailMatrix`). Each CSV row consumes the *next*
  // matrix row with that label so duplicate-label CSV input maps cleanly
  // to consecutive sub-rows instead of all overwriting the first one.
  const matrixRowIdxByLabel = new Map<string, number[]>()
  matrix.rows.forEach((mRow, idx) => {
    const labelKey = categoryGroupKeyForLabel(mRow.label)
    const list = matrixRowIdxByLabel.get(labelKey) ?? []
    list.push(idx)
    matrixRowIdxByLabel.set(labelKey, list)
  })
  const consumedIdxByLabel = new Map<string, number>()

  for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    if (!row.length) {
      continue
    }

    const labelKey = categoryGroupKeyForLabel(row[0])
    const indices = matrixRowIdxByLabel.get(labelKey)
    if (!indices || indices.length === 0) {
      continue
    }
    const consumed = consumedIdxByLabel.get(labelKey) ?? 0
    // Clamp to the last available sub-row if the CSV has more
    // duplicate-label rows than the matrix does — better to keep
    // overwriting the last sub-row than to drop the data silently.
    const matrixRowIndex = indices[Math.min(consumed, indices.length - 1)]
    consumedIdxByLabel.set(labelKey, consumed + 1)
    const matrixRow = matrix.rows[matrixRowIndex]

    for (let colIndex = 0; colIndex < columnKeys.length; colIndex++) {
      const columnIndex = matrix.columns.findIndex(
        col => normalizeLabelKey(col.componentName) === columnKeys[colIndex]
      )
      if (columnIndex < 0) {
        continue
      }

      const cell = matrixRow.cells[columnIndex]
      if (!cell) {
        continue
      }

      const raw = row[colIndex + 1]
      if (raw === undefined) {
        continue
      }
      const trimmed = raw.trim()
      if (trimmed === "") {
        continue
      }

      // Fan a single CSV cell out to every sibling node merged into this
      // column. Skipping socket-connected targets here matches the
      // single-node CSV path, which also refused to overwrite a connected
      // input. The cell's aggregate `socketConnected` is true iff *any*
      // sibling is connected, so we can't gate at the cell level — we
      // need per-target gating, otherwise one wired sibling would block
      // writes to its unconnected partners.
      for (const target of cell.targets) {
        if (target.socketConnected) {
          continue
        }
        updates.push({
          nodeId: target.nodeId,
          categoryValue: target.categoryValue,
          numericValue: trimmed,
        })
      }
    }
  }

  return updates
}

export function buildCategoricalDetailMatrixCsv(matrix: CategoricalDetailMatrix): string {
  const header = ["", ...matrix.columns.map(col => escapeCsvField(col.componentName))]
  const body = matrix.rows.map(row => {
    const cells = row.cells.map(cell => escapeCsvField(cell?.numericValue.trim() ?? ""))
    return [escapeCsvField(row.label), ...cells].join(",")
  })
  return [header.join(","), ...body].join("\n")
}

export function downloadCategoricalDetailMatrixCsv(matrix: CategoricalDetailMatrix, filename: string): void {
  const csvContent = buildCategoricalDetailMatrixCsv(matrix)
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename.endsWith(".csv") ? filename : `${filename}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
